import type { TextButtonProps } from '@bonniernews/dn-design-system-web/components/button/button-types.ts'
import { IconUse } from '@bonniernews/dn-design-system-web/components/icon-sprite/icon-sprite.tsx'
import Spinner from '@bonniernews/dn-design-system-web/components/spinner/spinner.tsx'

/**
 * - GitHub: [BonnierNews/dn-design-system/web/src/components/text-button](https://github.com/BonnierNews/dn-design-system/tree/main/web/src/components/text-button)
 * - Storybook: [TextButton](https://designsystem.dn.se/?path=/docs/basic-buttons-textbutton--docs)
 */
export const TextButton = ({
  text,
  href,
  type = 'button',
  iconName,
  iconPosition = 'none',
  size,
  fullWidth = false,
  mobileFullWidth = false,
  hideTextOnMobile = false,
  loading = false,
  disabled = false,
  forcePx = false,
  classNames = '',
  attributes = {},
}: TextButtonProps) => {
  const iconSvg =
    iconName && iconPosition !== 'none' ? <IconUse iconName={iconName} attributes={{ 'aria-hidden': 'true' }} /> : null

  const componentClassName = 'ds-text-btn'
  const classNamePrefix = `${componentClassName}--`
  const classes = [
    componentClassName,
    size && `${classNamePrefix}${size}`,
    fullWidth && `${classNamePrefix}full-width`,
    mobileFullWidth && `${classNamePrefix}mobile-full-width`,
    iconSvg && `${classNamePrefix}icon-${iconPosition}`,
    hideTextOnMobile && `${classNamePrefix}icon-only-mobile`,
    loading && 'ds-loading',
    forcePx && 'ds-force-px',
    classNames,
  ]
    .filter(Boolean)
    .join(' ')

  const ariaLabel = attributes['aria-label'] || text

  const spinnerSize = size === 'sm' ? 'xsmall' : 'small'
  const loadingHtml = loading ? (
    <Spinner size={spinnerSize} variant='primary' forcePx={forcePx} attributes={{ 'aria-hidden': 'true' }} />
  ) : null

  if (href) {
    return (
      <a href={href || '#'} aria-label={ariaLabel} className={classes} {...attributes}>
        <span className='ds-text-btn__text' aria-hidden='true'>
          {text}
        </span>
        {iconSvg}
        {loadingHtml}
      </a>
    )
  }

  return (
    <button type={type} aria-label={ariaLabel} className={classes} disabled={disabled} {...attributes}>
      <span className='ds-text-btn__text' aria-hidden='true'>
        {text}
      </span>
      {iconSvg}
      {loadingHtml}
    </button>
  )
}
